/**
 * Quotes Drawer — Slide-out panel showing saved quotes with search, load, archive, delete
 */

import { authHeaders } from './agent-identity.js';

const API_BASE = '';

/**
 * Build a rejection Error for a non-OK fetch response. Prefers the server's own
 * { error } message (netlify/functions/shared/response.js's error() shape) over a generic
 * fallback, and stamps `.status` so callers can special-case 401 (not signed in) vs other
 * failures without string-matching the message.
 * @param {Response} response
 * @param {string} fallbackMessage
 * @returns {Promise<Error>}
 */
async function fetchError(response, fallbackMessage) {
  const body = await response.json().catch(() => null);
  const err = new Error((body && body.error) || `${fallbackMessage}: ${response.status}`);
  err.status = response.status;
  return err;
}

/**
 * Fetch paginated quote list for the authenticated agent (NPN is derived server-side from
 * the Authorization token — see netlify/functions/quote-list.js — so it is never sent here).
 * @param {number} limit
 * @param {string|null} cursor - base64-encoded lastEvaluatedKey for pagination
 * @returns {{ quotes: Object[], nextCursor: string|null }}
 */
export async function fetchQuotes(limit = 25, cursor = null) {
  let url = `${API_BASE}/quotes/list?limit=${limit}`;
  if (cursor) url += `&cursor=${encodeURIComponent(cursor)}`;

  const response = await fetch(url, { headers: authHeaders() });
  if (!response.ok) throw await fetchError(response, 'Failed to fetch quotes');
  const result = await response.json();
  return result.data;
}

/**
 * Search the authenticated agent's quotes by client name (NPN derived server-side; see
 * netlify/functions/quote-search.js).
 * @param {string} clientName
 * @returns {{ quotes: Object[] }}
 */
export async function searchQuotes(clientName) {
  const url = `${API_BASE}/quotes/search?client=${encodeURIComponent(clientName)}`;
  const response = await fetch(url, { headers: authHeaders() });
  if (!response.ok) throw await fetchError(response, 'Search failed');
  const result = await response.json();
  return result.data;
}

/**
 * Fetch a single quote by ID, scoped to the authenticated agent (NPN derived server-side;
 * see netlify/functions/quote-get.js).
 * @param {string} quoteId
 * @returns {Object} Full quote object
 */
export async function fetchQuote(quoteId) {
  const url = `${API_BASE}/quotes/get?quoteId=${encodeURIComponent(quoteId)}`;
  const response = await fetch(url, { headers: authHeaders() });
  if (!response.ok) throw await fetchError(response, 'Failed to fetch quote');
  const result = await response.json();
  return result.data;
}

const STATUS_BADGES = {
  draft: { label: 'Draft', class: 'wq-badge--gray' },
  sent: { label: 'Sent', class: 'wq-badge--blue' },
  viewed: { label: 'Viewed', class: 'wq-badge--green' },
  converted: { label: 'Converted', class: 'wq-badge--gold' },
  archived: { label: 'Archived', class: 'wq-badge--muted' },
};

/**
 * Render a single quote list item
 */
function renderQuoteItem(quote) {
  const badge = STATUS_BADGES[quote.status] || STATUS_BADGES.draft;
  const date = new Date(quote.createdAt).toLocaleDateString();

  return `
    <div class="wq-quote-item" data-quote-id="${quote.quoteId}">
      <div class="wq-quote-item__header">
        <span class="wq-quote-item__name">${escapeForHtml(quote.clientName || 'Unnamed Client')}</span>
        <span class="wq-badge ${badge.class}">${badge.label}</span>
      </div>
      <div class="wq-quote-item__meta">
        ${quote.totalPlans || 0} plans &middot; ${date}
      </div>
      <div class="wq-quote-item__actions">
        <button class="wq-btn wq-btn--sm wq-btn--primary" data-action="load" data-quote-id="${quote.quoteId}">Load</button>
        <button class="wq-btn wq-btn--sm" data-action="email" data-quote-id="${quote.quoteId}">Email</button>
        <button class="wq-btn wq-btn--sm" data-action="share" data-quote-id="${quote.quoteId}">Share</button>
        <button class="wq-btn wq-btn--sm wq-btn--danger" data-action="delete" data-quote-id="${quote.quoteId}">Delete</button>
      </div>
    </div>
  `;
}

/**
 * Simple HTML escaping
 */
function escapeForHtml(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Initialize the drawer component
 * @param {HTMLElement} drawerEl - The drawer container element
 * @param {Object} callbacks - { onLoad, onEmail, onShare, onDelete }
 */
export function initDrawer(drawerEl, callbacks = {}) {
  let currentCursor = null;
  let isLoading = false;

  const listEl = drawerEl.querySelector('.wq-drawer__list');
  const searchEl = drawerEl.querySelector('.wq-drawer__search');
  const loadMoreEl = drawerEl.querySelector('.wq-drawer__load-more');

  // Debounced search
  let searchTimeout;
  if (searchEl) {
    searchEl.addEventListener('input', () => {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        const query = searchEl.value.trim();
        if (query.length >= 2) {
          callbacks.onSearch?.(query);
        } else if (query.length === 0) {
          callbacks.onRefresh?.();
        }
      }, 300);
    });
  }

  // Action button delegation
  if (listEl) {
    listEl.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-action]');
      if (!btn) return;
      const action = btn.dataset.action;
      const quoteId = btn.dataset.quoteId;
      callbacks[`on${action.charAt(0).toUpperCase() + action.slice(1)}`]?.(quoteId);
    });
  }

  // Load more
  if (loadMoreEl) {
    loadMoreEl.addEventListener('click', () => {
      if (currentCursor && !isLoading) {
        callbacks.onLoadMore?.(currentCursor);
      }
    });
  }

  return {
    renderQuotes(quotes, nextCursor, append = false) {
      currentCursor = nextCursor;
      const html = quotes.map(renderQuoteItem).join('');

      if (append) {
        listEl.insertAdjacentHTML('beforeend', html);
      } else {
        listEl.innerHTML = html || '<p class="wq-drawer__empty">No saved quotes yet.</p>';
      }

      if (loadMoreEl) {
        loadMoreEl.style.display = nextCursor ? 'block' : 'none';
      }
    },

    setLoading(loading) {
      isLoading = loading;
      drawerEl.classList.toggle('wq-drawer--loading', loading);
    },

    open() {
      drawerEl.classList.add('wq-drawer--open');
    },

    close() {
      drawerEl.classList.remove('wq-drawer--open');
    },

    toggle() {
      drawerEl.classList.toggle('wq-drawer--open');
    },
  };
}

export const QuotesDrawer = {
  fetchQuotes,
  searchQuotes,
  fetchQuote,
  initDrawer,
};
